import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowUpRight, FiGithub, FiExternalLink } from "react-icons/fi";
import "./Project.css";

const projects = [
  {
    id: "01",
    title: "JoyCalls",
    category: "Web Application",
    image: "/images/app-joycalls.png",
    description:
      "Real-time caregiver platform with AI-driven call experiences and subscription management.",
    fullDescription:
      "Led development of Joy Calls user pages with Next.js 13 App Router, MUI, Firebase Auth/Firestore, and Agora-powered call experiences. Caregivers can manage profiles, schedules, and AI-driven assessments in real time. Built end-to-end Stripe subscription management including card updates, cancel/reactivate flows with secure webhook filtering and live Firestore status sync.",
    tech: ["Next.js", "MUI", "Firebase", "Agora", "Stripe"],
    features: [
      "Real-time Call Experiences",
      "AI-driven Assessments",
      "Profile & Schedule Management",
      "Stripe Subscriptions",
      "Pixel-accurate Figma UI",
    ],
    color: "#A40E34",
    liveUrl: "#",
    githubUrl: "#",
    year: "2025",
  },
  {
    id: "02",
    title: "Floraria Hellen",
    category: "E-Commerce",
    image: "/images/hellenproparty.png",
    description:
      "Full-stack florist e-commerce with localization, Stripe checkout, and delivery pricing.",
    fullDescription:
      "A florist e-commerce site with RO/EN localization and media-rich product/event catalogs built on Next.js 16 + Prisma/PostgreSQL, serving imagery via Cloudinary. Features secure Stripe Payment Intents checkout with add-ons/extras persisted across sessions, Google Maps–based distance calculation for auto-priced delivery, and admin dashboards with transactional emails via Resend.",
    tech: ["Next.js", "Prisma", "PostgreSQL", "Stripe", "Cloudinary"],
    features: [
      "RO/EN Localization",
      "Stripe Payment Intents",
      "Google Maps Delivery Pricing",
      "Admin Dashboards",
      "Transactional Emails",
    ],
    color: "#C4183F",
    liveUrl: "#",
    githubUrl: "#",
    year: "2025",
  },
  {
    id: "03",
    title: "Steel Tech Website",
    category: "Marketing Site",
    image: "/images/steeltech.png",
    description:
      "Responsive marketing site for industrial services with data-driven content sections.",
    fullDescription:
      "A responsive marketing site highlighting hydraulic hose crimping, plasma cutting, and laser cutting services. Features data-driven service sections and photo carousels to showcase equipment specs and processes, with content maintained via shared constants. Elevated credibility with CAD/CAM capabilities, custom solution highlights, and partner logo showcases.",
    tech: ["Next.js", "Tailwind CSS", "Static Site"],
    features: [
      "Service Showcases",
      "Photo Carousels",
      "Data-driven Content",
      "Partner Logos",
      "Responsive Design",
    ],
    color: "#8B0A2A",
    liveUrl: "#",
    githubUrl: "#",
    year: "2024",
  },
  {
    id: "04",
    title: "Achip Consulting",
    category: "Business Website",
    image: "/images/achipconsulting.png",
    description:
      "Public procurement consulting platform with service packages and online workflow.",
    fullDescription:
      "A professional consulting website for Achip Consulting, specializing in public procurement support for Romanian businesses. Features clear service tiers (Start, Active, Pro, Complex), a step-by-step online workflow section, and full SEAP administration guidance. Built with a clean, modern design emphasizing trust and clarity for B2B clients.",
    tech: ["Next.js", "Tailwind CSS", "Responsive Design"],
    features: [
      "Service Package Tiers",
      "Online Workflow Process",
      "SEAP Administration Guide",
      "B2B Lead Capture",
      "100% Online Consulting",
    ],
    color: "#D4244E",
    liveUrl: "https://achipconsulting.com",
    githubUrl: "#",
    year: "2025",
  },
];

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const openProject = (project) => {
    setSelectedProject(project);
    document.body.style.overflow = "hidden";
  };

  const closeProject = () => {
    setSelectedProject(null);
    document.body.style.overflow = "";
  };

  return (
    <section id="projects" className="projects">
      <div className="projects-container">
        {/* Header */}
        <div className="projects-header">
          <div className="header-left">
            <span className="label-text">Selected Work</span>
            <h2 className="projects-title">
              <span className="title-line">Let's</span>
              <span className="title-line">Explore</span>
            </h2>
          </div>
          <div className="header-right">
            <h2 className="projects-title-alt">
              <span className="title-line">My</span>
              <span className="title-line">Projects</span>
            </h2>
          </div>
        </div>

        {/* Projects List */}
        <div className="projects-list">
          {projects.map((project) => (
            <article
              key={project.id}
              className="project-item"
              onClick={() => openProject(project)}
              style={{ "--project-color": project.color }}
            >
              <div className="project-visual">
                {project.image
                  ? <img src={project.image} alt={project.title} className="project-img" />
                  : <span className="project-id">{project.id}</span>
                }
                <div className="visual-hover">
                  <span>View Project</span>
                  <FiArrowUpRight />
                </div>
              </div>
              <div className="project-info">
                <span className="project-category">{project.category}</span>
                <h3 className="project-name">{project.title}</h3>
                <p className="project-desc">{project.description}</p>
                <div className="project-tech">
                  {project.tech.slice(0, 3).map((t) => (
                    <span key={t}>{t}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* View All */}
        <div className="projects-footer">
          <a
            href="https://github.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline"
          >
            View All on GitHub
            <FiGithub />
          </a>
        </div>
      </div>

      {/* Project Detail Panel */}
      <AnimatePresence>
        {selectedProject && (
          <>
            <motion.div
              className="project-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeProject}
            />

            <motion.div
              className="project-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              style={{ "--project-color": selectedProject.color }}
            >
              <button className="panel-close" onClick={closeProject}>
                <span>← Back</span>
              </button>

              <div className="panel-content">
                <div className="panel-header">
                  <span className="panel-number">{selectedProject.id}</span>
                  <span className="panel-category">
                    {selectedProject.category}
                  </span>
                  <span className="panel-year">{selectedProject.year}</span>
                </div>

                <h2 className="panel-title">{selectedProject.title}</h2>
                <p className="panel-description">
                  {selectedProject.fullDescription}
                </p>

                {selectedProject.image && (
                  <img src={selectedProject.image} alt={selectedProject.title} className="panel-img" />
                )}

                <div className="panel-features-row">
                  <div className="panel-section">
                    <h4 className="panel-section-title">Key Features</h4>
                    <ul className="panel-features">
                      {selectedProject.features.map((feature) => (
                        <li key={feature}>
                          <span className="feature-dot" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="visual-circle">
                    <span>{selectedProject.id}</span>
                  </div>
                </div>

                <div className="panel-section">
                  <h4 className="panel-section-title">Technologies</h4>
                  <div className="panel-tech">
                    {selectedProject.tech.map((tech) => (
                      <span key={tech} className="tech-badge">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="panel-actions">
                  <a
                    href={selectedProject.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="panel-btn primary"
                  >
                    <FiExternalLink />
                    View Live Site
                  </a>
                  <a
                    href={selectedProject.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="panel-btn secondary"
                  >
                    <FiGithub />
                    Source Code
                  </a>
                </div>
              </div>

              <div className="panel-visual" />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
